// Constants
import { HttpStatus } from "@ms/uno-constants/lib/local/HttpStatusConstants";
import { StoreEntityState } from "@ms/uno-constants/lib/local/StoreConstants";
// Utilities
import { Temporal } from "temporal-polyfill-lite";
/**
 * Get the state based on a request response
 * @param response Response from the request
 */ export function getStateBasedOnResponse(response) {
    if (response.status === HttpStatus.NotFound) {
        return StoreEntityState.NotFound;
    }
    if (response.status === HttpStatus.Forbidden) {
        return StoreEntityState.Inaccessible;
    }
    return null;
}
/**
 * Non-standard status code (Client Closed Request) the delta endpoint can return when the delta
 * token has timed out. Treated equivalently to 410 Gone for delta-token expiry.
 */ const DeltaTokenTimeoutStatusCode = 499;
/**
 * Returns whether a response status code indicates an expired/stale delta token, meaning the delta
 * link can no longer be used and the collection must be re-fetched in full to recover.
 * @param status The HTTP status code from the failed delta request (may be undefined)
 */ export function isStaleDeltaTokenStatus(status) {
    return status === DeltaTokenTimeoutStatusCode || status === HttpStatus.Gone;
}
/**
 * Get whether the state represents a failed state
 * @param state state to check
 */ export function isStateFailure(state) {
    return state === StoreEntityState.NotFound || state === StoreEntityState.Inaccessible || state === StoreEntityState.UnableToFetch;
}
/**
 * Update the status of an entity in the table
 * @param table The table containing entity statuses
 * @param id The identifier for the entity
 * @param newStatus The new status to set for the entity
 * @returns Whether the status was updated
 */ export function updateStatus(table, id, statusUpdates) {
    if (statusUpdates.isFetching === undefined && statusUpdates.state === undefined && statusUpdates.deltaSyncState === undefined) {
        return false; // No updates to apply
    }
    const currentStatus = table.get(id);
    const isFetchingUpdated = !currentStatus || statusUpdates.isFetching !== undefined && statusUpdates.isFetching !== currentStatus.isFetching;
    const stateUpdated = !currentStatus || statusUpdates.state !== undefined && statusUpdates.state !== currentStatus.state;
    const isDeltaSyncStateUpdated = statusUpdates.deltaSyncState !== undefined && (statusUpdates.deltaSyncState.link !== currentStatus?.deltaSyncState?.link || statusUpdates.deltaSyncState.backoffErrorStreak !== currentStatus?.deltaSyncState?.backoffErrorStreak || statusUpdates.deltaSyncState.emptyStreak !== currentStatus?.deltaSyncState?.emptyStreak);
    table.set(id, {
        isFetching: statusUpdates.isFetching ?? currentStatus?.isFetching ?? false,
        state: statusUpdates.state ?? currentStatus?.state ?? StoreEntityState.Absent,
        deltaSyncState: statusUpdates.deltaSyncState ?? currentStatus?.deltaSyncState,
        // Honor an explicitly supplied timestamp (e.g. a persisted fetch time restored during rehydration)
        // so TTL/freshness evaluation reflects the true last-fetch time; otherwise stamp the current instant.
        timestamp: statusUpdates.timestamp ?? Temporal.Now.instant()
    });
    return isFetchingUpdated || stateUpdated || isDeltaSyncStateUpdated;
}
/**
 * Helper method to verify the fetch for an entity store can be done
 * @param status Entity store status
 */ export function isEligibleToFetch(status) {
    // If there is no ongoing fetch and either:
    //   - data is Absent (never fully fetched; we also set Absent between pages of a paged fetch), or
    //   - data is Dirty (cached data for this entity cannot be trusted — e.g. a delta sync on a
    //     collection containing it returned 410 Gone because the service rolled its etag scheme —
    //     and must be re-hydrated; the Dirty bit clears after the next successful upsert).
    return !status?.isFetching && (status?.state === StoreEntityState.Absent || status?.state === StoreEntityState.Dirty);
}
/**
 * Default status for an unfetched store entity.
 * This status indicates that the entity has not been fetched yet.
 */ export const AbsentStoreEntityStatus = {
    isFetching: false,
    state: StoreEntityState.Absent,
    timestamp: null
};
/**
 * Check if the given state is a terminal failure state
 * @param state The state to check
 */ export function isTerminalFailureState(state) {
    return state === StoreEntityState.NotFound || state === StoreEntityState.Inaccessible;
}
